<div class="min-h-screen bg-slate-50 pb-8">
  <!-- Screen-reader only live status announcer -->
  <div class="sr-only" aria-live="polite">{{ liveStatusAnnouncement }}</div>

  <!-- ====================================================== -->
  <!-- PAGE HEADER -->
  <!-- ====================================================== -->

  <header
    class="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
  >
    <div
      class="h-1.5 bg-gradient-to-r from-[#1E2A78] via-indigo-500 to-violet-500"
    ></div>

    <div
      class="flex flex-col gap-5 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="min-w-0">
        <div class="flex items-center gap-3">
          <div
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-[#1E2A78]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1.8"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5A3.375 3.375 0 0 0 10.125 2.25H8.25m0 12.75h7.5m-7.5 3h4.5m-9-13.5v15.75A1.5 1.5 0 0 0 5.25 21.75h13.5a1.5 1.5 0 0 0 1.5-1.5V9.108c0-.398-.158-.779-.44-1.06l-5.858-5.858a1.5 1.5 0 0 0-1.06-.44H5.25a1.5 1.5 0 0 0-1.5 1.5Z"
              />
            </svg>
          </div>

          <div class="min-w-0">
            <p
              class="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-500"
            >
              {{ 'DOC_BUILDER.TITLE' | translate }}
              <span *ngIf="crdtPrpslSeq" class="text-slate-400"
                >{{ 'DOC_BUILDER.PROPOSAL_NO' | translate: { seq: crdtPrpslSeq } }}</span
              >
            </p>

            <h1
              class="mt-1 truncate text-2xl font-bold tracking-tight text-slate-900"
            >
              {{ sectionTitleKey(template.name) | translate }}
            </h1>

            <p class="mt-1 text-sm text-slate-500">
              {{ 'DOC_BUILDER.SUBTITLE' | translate }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <app-back-button></app-back-button>

        <button
          type="button"
          [disabled]="!crdtPrpslSeq"
          (click)="viewReport()"
          class="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-5 text-sm font-semibold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.269 2.943 9.542 7-1.273 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z"
            />

            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>

          {{ 'DOC_BUILDER.VIEW_REPORT' | translate }}
        </button>
      </div>
    </div>
  </header>

  <!-- ====================================================== -->
  <!-- MISSING PROPOSAL SEQUENCE -->
  <!-- ====================================================== -->

  <div
    *ngIf="proposalSequenceError"
    role="alert"
    class="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5"
  >
    <div class="flex items-start gap-3">
      <div
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"
        aria-hidden="true"
      >
        !
      </div>

      <div>
        <h3 class="font-semibold text-red-800">
          {{ 'DOC_BUILDER.SEQUENCE_MISSING' | translate }}
        </h3>

        <p class="mt-1 text-sm text-red-700">{{ proposalSequenceError | translate }}</p>
      </div>
    </div>
  </div>

  <!-- ====================================================== -->
  <!-- CURRENT SECTION AND PROGRESS -->
  <!-- ====================================================== -->

  <div
    *ngIf="enabledSections.length"
    class="mb-5 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
  >
    <div
      class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex min-w-0 items-center gap-4">
        <div
          class="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-[#1E2A78] text-white shadow-sm"
        >
          <span class="text-base font-bold">
            {{ activeSectionIndex + 1 }}
          </span>

          <span class="text-[9px] uppercase opacity-75">
            {{ 'DOC_BUILDER.OF_TOTAL' | translate: { total: enabledSections.length } }}
          </span>
        </div>

        <div class="min-w-0">
          <p
            class="text-xs font-semibold uppercase tracking-[0.14em] text-indigo-500"
          >
            {{ 'DOC_BUILDER.SECTION_X_OF_Y' | translate: { current: activeSectionIndex + 1, total: enabledSections.length } }}
          </p>

          <h2
            id="current-section-heading"
            tabindex="-1"
            class="mt-1 truncate rounded text-base font-bold text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
          >
            {{ sectionTitleKey(currentSection?.title) | translate }}
          </h2>

          <p
            *ngIf="currentSection"
            class="mt-1 flex items-center gap-1.5 text-xs font-medium"
            [ngClass]="{
              'text-slate-400': stateService.stateFor(currentSection.id).status === 'idle',
              'text-amber-600': stateService.stateFor(currentSection.id).status === 'dirty',
              'text-indigo-600': stateService.stateFor(currentSection.id).status === 'saving',
              'text-emerald-600': stateService.stateFor(currentSection.id).status === 'saved',
              'text-red-600':
                stateService.stateFor(currentSection.id).status === 'validation-error' ||
                stateService.stateFor(currentSection.id).status === 'api-error'
            }"
          >
            <ng-container [ngSwitch]="stateService.stateFor(currentSection.id).status">
              <span *ngSwitchCase="'dirty'">{{ 'DOC_BUILDER.STATUS_UNSAVED' | translate }}</span>
              <span *ngSwitchCase="'saving'">{{ 'DOC_BUILDER.STATUS_SAVING' | translate }}</span>
              <span *ngSwitchCase="'saved'">{{ 'DOC_BUILDER.STATUS_SAVED' | translate }}</span>
              <span *ngSwitchCase="'validation-error'">
                ⚠ {{ (currentSectionMessage | translate) || ('DOC_BUILDER.FIX_HIGHLIGHTED' | translate) }}
              </span>
              <span *ngSwitchCase="'api-error'">
                ⚠ {{ (currentSectionMessage | translate) || ('DOC_BUILDER.SAVE_FAILED' | translate) }}
              </span>
              <span *ngSwitchDefault>{{ 'DOC_BUILDER.NOT_SAVED_YET' | translate }}</span>
            </ng-container>
          </p>
        </div>
      </div>

      <div class="w-full sm:w-72">
        <div
          class="mb-2 flex items-center justify-between text-xs"
        >
          <span class="font-medium text-slate-500">
            {{ 'DOC_BUILDER.SAVED_PROGRESS' | translate }}
          </span>

          <span class="font-bold text-[#1E2A78]">
            {{ savedProgressPercentage }}%
          </span>
        </div>

        <div
          class="h-2.5 overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="savedProgressPercentage"
          [attr.aria-label]="'DOC_BUILDER.SAVED_PROGRESS' | translate"
        >
          <div
            class="h-full rounded-full bg-gradient-to-r from-[#1E2A78] via-indigo-500 to-violet-500 transition-all duration-500"
            [style.width.%]="savedProgressPercentage"
          ></div>
        </div>

        <p class="mt-2 text-end text-[11px] font-medium text-slate-400">
          {{ 'DOC_BUILDER.SAVED_SECTIONS_COUNT' | translate: { saved: stateService.savedCount(), total: enabledSections.length } }}
        </p>
      </div>
    </div>
  </div>

  <!-- ====================================================== -->
  <!-- DOCUMENT TABS -->
  <!-- ====================================================== -->

  <div
    *ngIf="enabledSections.length"
    class="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
  >
    <app-document-tabs
      [tabs]="enabledSections"
      [activeIndex]="activeSectionIndex"
      [statuses]="sectionStatuses"
      panelId="document-section-content"
      (activeIndexChange)="onActiveTabChange($event)"
    >
    </app-document-tabs>
  </div>

  <!-- ====================================================== -->
  <!-- SECTION CONTENT -->
  <!-- ====================================================== -->

  <main
    id="document-section-content"
    role="tabpanel"
    tabindex="0"
    [attr.aria-labelledby]="currentSection ? 'tab-' + currentSection.id : null"
    class="scroll-mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6"
    (input)="onSectionContentActivity()"
    (change)="onSectionContentActivity()"
    (click)="onSectionContentActivity()"
  >
    <app-section-renderer
      *ngIf="
        currentSection &&
        crdtPrpslSeq;
        else emptySectionState
      "
      [section]="currentSection"
      [isEditMode]="isEditMode"
      [showHeading]="false"
    >
    </app-section-renderer>

    <ng-template #emptySectionState>
      <div
        class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center"
      >
        <div
          class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5A3.375 3.375 0 0 0 10.125 2.25H8.25m0 12.75h7.5m-7.5 3h4.5m-9-13.5v15.75A1.5 1.5 0 0 0 5.25 21.75h13.5a1.5 1.5 0 0 0 1.5-1.5V9.108c0-.398-.158-.779-.44-1.06l-5.858-5.858a1.5 1.5 0 0 0-1.06-.44H5.25a1.5 1.5 0 0 0-1.5 1.5Z"
            />
          </svg>
        </div>

        <h3 class="mt-4 font-semibold text-slate-700">
          {{ (enabledSections.length ? 'DOC_BUILDER.SEQUENCE_REQUIRED' : 'DOC_BUILDER.NO_SECTION_AVAILABLE') | translate }}
        </h3>

        <p class="mt-2 text-sm text-slate-500">
          {{
            (enabledSections.length
              ? 'DOC_BUILDER.SECTION_CANNOT_LOAD'
              : 'DOC_BUILDER.NO_ENABLED_SECTION') | translate
          }}
        </p>
      </div>
    </ng-template>
  </main>

  <!-- ====================================================== -->
  <!-- FLOATING SECTION NAVIGATOR -->
  <!-- ====================================================== -->

  <div
    *ngIf="
      enabledSections.length &&
      crdtPrpslSeq
    "
    class="sticky bottom-4 z-30 mt-8 px-1"
  >
    <div
      class="mx-auto max-w-6xl overflow-hidden rounded-[26px] border border-indigo-100 bg-white/95 shadow-[0_20px_60px_rgba(30,42,120,0.18)] backdrop-blur-xl"
    >
      <!-- Top progress line -->
      <div class="h-1.5 w-full bg-slate-100">
        <div
          class="h-full rounded-e-full bg-gradient-to-r from-[#1E2A78] via-indigo-500 to-violet-500 transition-all duration-500"
          [style.width.%]="savedProgressPercentage"
        ></div>
      </div>

      <div
        class="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:p-5"
      >
        <!-- Previous Section -->
        <button
          type="button"
          [disabled]="!canGoBack || isSavingAnything"
          (click)="goToPreviousSection()"
          class="group flex min-h-[76px] items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-start transition hover:border-indigo-200 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-35"
        >
          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition group-hover:-translate-x-0.5 group-hover:border-indigo-200 group-hover:text-indigo-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m15 19-7-7 7-7"
              />
            </svg>
          </span>

          <span class="min-w-0">
            <span
              class="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400"
            >
              {{ 'DOC_BUILDER.PREVIOUS_SECTION' | translate }}
            </span>

            <span
              class="mt-1 block truncate text-sm font-semibold text-slate-700"
            >
              {{
                (previousSection ? (sectionTitleKey(previousSection.title) | translate) : null) ||
                ('DOC_BUILDER.FIRST_SECTION' | translate)
              }}
            </span>
          </span>
        </button>

        <!-- Centre Section Indicator -->
        <div
          class="flex items-center justify-center gap-4 px-2"
        >
          <div class="relative shrink-0">
            <div
              class="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#1E2A78] to-violet-500 p-[3px] shadow-lg shadow-indigo-200"
            >
              <div
                class="flex h-full w-full flex-col items-center justify-center rounded-full bg-white"
              >
                <span
                  class="text-lg font-bold text-[#1E2A78]"
                >
                  {{ activeSectionIndex + 1 }}
                </span>

                <span
                  class="text-[9px] font-semibold uppercase text-slate-400"
                >
                  {{ 'DOC_BUILDER.OF_TOTAL' | translate: { total: enabledSections.length } }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="hidden max-w-[230px] text-center sm:block"
          >
            <p
              class="text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-500"
            >
              {{ 'DOC_BUILDER.SAVED_PROGRESS' | translate }}
            </p>

            <p class="mt-1 text-xs text-slate-400">
              {{ 'DOC_BUILDER.PROGRESS_SAVED' | translate: { percent: savedProgressPercentage, saved: stateService.savedCount(), total: enabledSections.length } }}
            </p>
          </div>
        </div>

        <!-- Next Section -->
        <button
          type="button"
          [disabled]="!canGoNext || isSavingAnything"
          (click)="goToNextSection()"
          class="group flex min-h-[76px] items-center justify-end gap-3 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-3 text-end transition hover:border-indigo-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-35"
        >
          <span class="min-w-0">
            <span
              class="block text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-400"
            >
              {{ 'DOC_BUILDER.NEXT_SECTION' | translate }}
            </span>

            <span
              class="mt-1 block truncate text-sm font-semibold text-[#1E2A78]"
            >
              {{
                (nextSection ? (sectionTitleKey(nextSection.title) | translate) : null) ||
                ('DOC_BUILDER.FINAL_SECTION' | translate)
              }}
            </span>
          </span>

          <span
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1E2A78] text-white shadow-md transition group-hover:translate-x-0.5 group-hover:bg-[#16205e]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </span>
        </button>
      </div>

      <!-- Bottom Action Strip -->
      <div
        class="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between lg:px-5"
      >
        <div class="flex items-center gap-2">
          <span
            *ngIf="isSavingAnything; else saveCautionIcon"
            class="h-2 w-2 shrink-0 rounded-full bg-indigo-500 animate-pulse"
            aria-hidden="true"
          ></span>

          <ng-template #saveCautionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 shrink-0 text-amber-500"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clip-rule="evenodd"
              />
            </svg>
          </ng-template>

          <p class="text-xs text-slate-500">
            {{
              (isSavingAnything
                ? 'DOC_BUILDER.SAVING_CHANGES'
                : 'DOC_BUILDER.SAVE_BEFORE_MOVING') | translate
            }}
          </p>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row">
          <!-- Save Draft (not on the last section, which only shows the saved plan) -->
          @if (isEditMode && !isLastSection && !isDocumentsSection) {
            <button
              type="button"
              [disabled]="isSavingAnything"
              (click)="saveCurrentSection()"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z"
                />

                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 21v-8h6v8M9 3v5h5"
                />
              </svg>

              {{
                (isSavingAnything
                  ? 'COMMON.SAVING'
                  : 'DOC_BUILDER.SAVE_DRAFT') | translate
              }}
            </button>
          }

          <!-- Save and Continue -->
          <button
            *ngIf="
              isEditMode &&
              canGoNext &&
              !isDocumentsSection
            "
            type="button"
            [disabled]="isSavingAnything"
            (click)="saveAndGoNext()"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1E2A78] to-indigo-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            {{ 'DOC_BUILDER.SAVE_AND_CONTINUE_BTN' | translate }}

            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </button>

          <!-- Submit Credit Proposal -->
          @if (isEditMode && isLastSection && showSubmitCreditProposal) {
            <button
              type="button"
              (click)="openSubmitCreditProposalPopup()"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#1E2A78] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#16205e]"
            >
              {{ 'DOC_BUILDER.SUBMIT_CREDIT_PROPOSAL' | translate }}
            </button>
          }

          <!-- Continue: view mode, and the Documents tab (uploads save themselves) -->
          <button
            *ngIf="
              (!isEditMode || isDocumentsSection) &&
              canGoNext
            "
            type="button"
            (click)="goToNextSection()"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1E2A78] to-indigo-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
          >
            {{ 'COMMON.CONTINUE' | translate }}

            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </button>

          <!-- View Mode Final Action -->
          <button
            *ngIf="
              !isEditMode &&
              isLastSection
            "
            type="button"
            (click)="viewReport()"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
          >
            {{ 'DOC_BUILDER.REVIEW_FINAL_REPORT' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Extra scroll room so the sticky navigator never overlaps the last field -->
  <div class="h-24" aria-hidden="true"></div>

  <!-- ====================================================== -->
  <!-- UNSAVED CHANGES DIALOG -->
  <!-- ====================================================== -->

  <app-popup
    [show]="showUnsavedDialog"
    type="custom"
    title="DOC_BUILDER.UNSAVED_CHANGES"
    (showChange)="!$event && resolveUnsavedDialog('stay')"
  >
    <p class="text-sm text-slate-600">
      {{ 'DOC_BUILDER.UNSAVED_CHANGES_MSG' | translate: { title: (sectionTitleKey(currentSection?.title) | translate) } }}
    </p>

    <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
      <button
        type="button"
        (click)="resolveUnsavedDialog('stay')"
        class="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
      >
        {{ 'DOC_BUILDER.STAY_ON_SECTION' | translate }}
      </button>

      <button
        type="button"
        (click)="resolveUnsavedDialog('discard')"
        class="inline-flex h-10 items-center justify-center rounded-xl border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 transition hover:bg-red-50"
      >
        {{ 'DOC_BUILDER.DISCARD_CHANGES' | translate }}
      </button>

      <button
        type="button"
        (click)="resolveUnsavedDialog('save')"
        class="inline-flex h-10 items-center justify-center rounded-xl bg-[#1E2A78] px-4 text-sm font-semibold text-white transition hover:bg-[#16205e]"
      >
        {{ 'DOC_BUILDER.SAVE_AND_CONTINUE' | translate }}
      </button>
    </div>
  </app-popup>

  <!-- ====================================================== -->
  <!-- SUBMIT CREDIT PROPOSAL -->
  <!-- ====================================================== -->

  <app-popup
    [show]="showSubmitProposalPopup"
    type="custom"
    title="DOC_BUILDER.SUBMIT_CREDIT_PROPOSAL"
    (showChange)="!$event && closeSubmitCreditProposalPopup()"
  >
    <label for="submit-proposal-comment" class="text-sm font-medium text-slate-700">
      {{ 'DOC_BUILDER.COMMENT' | translate }}
    </label>

    <textarea
      id="submit-proposal-comment"
      rows="3"
      [(ngModel)]="submitProposalComment"
      name="submitProposalComment"
      class="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
    ></textarea>

    <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
      <button
        type="button"
        [disabled]="isSubmittingCreditProposal"
        (click)="closeSubmitCreditProposalPopup()"
        class="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ 'COMMON.CANCEL' | translate }}
      </button>

      <button
        type="button"
        [disabled]="isSubmittingCreditProposal"
        (click)="confirmSubmitCreditProposal()"
        class="inline-flex h-10 items-center justify-center rounded-xl bg-[#1E2A78] px-4 text-sm font-semibold text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ (isSubmittingCreditProposal ? 'DOC_BUILDER.SUBMITTING' : 'COMMON.SUBMIT') | translate }}
      </button>
    </div>
  </app-popup>
</div>
